import { useCallback } from 'react';

import { useSpecialAwardForm } from '../../calculations-step/hooks/use-special-award-form';
import type { UseSpecialAwardFormResult } from '../../calculations-step/hooks/use-special-award-form';
import { useSpecialAwardModalData } from '../../calculations-step/hooks/use-special-award-modal-data';
import type { UseSpecialAwardModalDataResult } from '../../calculations-step/hooks/use-special-award-modal-data';

import {
  buildPopulationName,
  toPopulationDefinition,
  toPopulationRule,
} from '../helpers/custom-population';
import type { DraftPopulation } from '../helpers/custom-population';

import type { CustomPopulationRequest } from 'api/generated/schemas';

interface UseCustomPopulationFormParams {
  userId: string;
  modelId: string | number;
  clientId: string | number;
  population?: DraftPopulation;
  onSubmit: (definition: CustomPopulationRequest) => void;
}

export interface UseCustomPopulationFormResult {
  form: UseSpecialAwardFormResult;
  data: UseSpecialAwardModalDataResult;
  canSubmit: boolean;
  submit: () => void;
}

const NO_REGIMES: never[] = [];

export const useCustomPopulationForm = ({
  userId,
  modelId,
  clientId,
  population,
  onSubmit,
}: UseCustomPopulationFormParams): UseCustomPopulationFormResult => {
  const data = useSpecialAwardModalData({ userId, modelId, clientId });

  const form = useSpecialAwardForm({
    regimes: NO_REGIMES,
    customLogicCategories: data.customLogicCategories,
    initialRule: population ? toPopulationRule(population) : undefined,
    isPrefillReady: data.isPrefillReady,
  });

  const { isValid, toRequests, appliesTo, criteriaBlocks } = form;
  const { customLogicCategories } = data;

  const submit = useCallback(() => {
    const [request] = toRequests();

    if (!isValid || !request) return;

    onSubmit(
      toPopulationDefinition(
        buildPopulationName(appliesTo, criteriaBlocks, customLogicCategories),
        request
      )
    );
  }, [
    isValid,
    toRequests,
    appliesTo,
    criteriaBlocks,
    customLogicCategories,
    onSubmit,
  ]);

  return { form, data, canSubmit: isValid, submit };
};
